import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { faHeart } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Card, Divider, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { Switch } from '@sage/ui-components';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import { ObjectTypes, useAnalytics } from 'src/AnalyticsContext';
import ResidentAvatar from 'src/component-library/ResidentAvatar';
import SettingContainer from 'src/component-library/SettingContainer';
import HospiceChip from 'src/components/fragments/HospiceChip';
import ResuscitationChip from 'src/components/fragments/ResuscitationChip';
import RoutineCheckChip from 'src/components/fragments/RoutineCheckChip';
import UnitCheckInCard from 'src/components/fragments/UnitCheckInCard';
import ResidentCareInfo from 'src/components/residents/ResidentCareInfo';
import { CareTypeLabelsAbbreviated, CareTypes } from 'src/constants/CareTypes';
import { CheckInStatuses } from 'src/constants/CheckIn';
import { Drawers } from 'src/constants/Drawers';
import { ResuscitationStrategyType } from 'src/constants/ResuscitationStrategies';
import { useCareAssignments } from 'src/contexts/CareAssignmentsContext';
import useCareTypeFeatures from 'src/hooks/useCareTypeFeatures';
import useCheckIns from 'src/hooks/useCheckIns';
import useFacilityFeatures from 'src/hooks/useFacilityFeatures';
import useResidentCareInfoDisplays from 'src/hooks/useResidentCareInfoDisplays';
import useUserPermissions from 'src/hooks/useUserPermissions';
import { selectRoutineCheckInterval } from 'src/store/configSlice';
import {
    CheckIn,
    Resident,
    useGetLatestCareActivityQuery,
    useGetResidentQuery,
} from 'src/store/sageAdminApi';
import { validateHospiceStatus } from 'src/utils/HospiceUtils';
import { getTimeSinceLastCareActivity } from 'src/utils/latestCareTimeUtils';
import { getResidentDisplayName } from 'src/utils/residentUtils';
import { getRoutineCheckProgress } from 'src/utils/routineCheckUtils';
import { snackActions } from 'src/utils/snackBarTools';

export interface ResidentDetailsDrawerProps extends DrawerComponentProps {
    resident: Resident;
}

const DrawerName = 'Resident Details Drawer';

function ResidentDetailsDrawer(props: ResidentDetailsDrawerProps) {
    const { hasNextDrawer, onClose, onNext, resident } = props;

    const { palette } = useTheme();
    const navigate = useNavigate();

    const { trackEvent } = useAnalytics();
    const { isUsingCommuniCareResolutionFlow } = useFacilityFeatures();

    const routineCheckInterval = useSelector(selectRoutineCheckInterval);
    const { getFeatures } = useCareTypeFeatures();

    const { id: residentId, age, unit } = resident;
    const residentName = getResidentDisplayName(resident);
    const unitIsIL = unit?.careType === CareTypes.IndependentLiving;
    const unitIsCheckInEligible = getFeatures(unit?.careType).checkIns.isEnabled;

    const { canSeePHI } = useUserPermissions();
    const { addToPinned, removeFromPinned, pinnedResidentIds } = useCareAssignments();
    const isPinned = pinnedResidentIds?.has(residentId!);

    const { data: residentData, isLoading: isLoadingResident } = useGetResidentQuery(
        { residentId: residentId! },
        { skip: !residentId }
    );

    const isOnHold = !!resident?.isOnHold;
    const isArchived = !!resident?.isArchived;
    const isActive = !isOnHold && !isArchived;

    const residentUnitId = residentData?.unit?.id;

    const showActivityProgress =
        resident?.isRoutineCheckEnabled && routineCheckInterval > 0 && isActive && !unitIsIL;

    const isTaskingEnabled = getFeatures(unit?.careType).ehr.isTaskingEnabled;

    const { minSinceLastActivity, isLoading: isLoadingCareActivity } =
        useGetLatestCareActivityQuery(
            { getLatestCareActivityRequest: { residentRids: [residentId!] } },
            {
                skip: !residentId,
                pollingInterval: showActivityProgress ? 60_000 : 0,
                selectFromResult: (results) => ({
                    ...results,
                    minSinceLastActivity: getTimeSinceLastCareActivity(resident, results.data),
                }),
            }
        );

    const { checkIns } = useCheckIns({ unitId: residentUnitId });

    const { careInfoDisplays } = useResidentCareInfoDisplays(resident);

    const unitIsCheckedIn =
        checkIns?.some(
            (checkIn: CheckIn) =>
                checkIn?.unit?.id === residentUnitId && checkIn?.status === CheckInStatuses.Complete
        ) || false;

    const { hospiceHealthInfo, resuscitationStrategy } = residentData?.healthInfo || {};

    const handleTogglePinnedResident = () => {
        if (isActive) {
            if (isPinned) {
                trackEvent('Remove From Care Assignment Clicked', {
                    objectType: ObjectTypes.Button,
                    residentId,
                    context: DrawerName,
                });

                removeFromPinned(residentId!);
                snackActions.success('Resident Removed From Care Assignment', {
                    autoHideDuration: 2_000,
                });
            } else {
                trackEvent('Add To Care Assignment Clicked', {
                    objectType: ObjectTypes.Button,
                    residentId,
                    context: DrawerName,
                });

                addToPinned(residentId!);
                snackActions.success('Resident Added To Care Assignment', {
                    autoHideDuration: 2_000,
                });
            }
        }
    };

    const handleClickViewProfile = () => {
        trackEvent('View Profile Button Clicked', { residentId, context: DrawerName });
        onClose();

        if (residentId) {
            navigate(`/residents/resident/${residentId}`);
        }
    };

    const handleClickLogCare = () => {
        trackEvent('Log Care Button Clicked', {
            objectType: ObjectTypes.Button,
            residentId,
            context: DrawerName,
        });

        if (isTaskingEnabled) {
            navigate(`/residents/resident/${residentId}/care`);
            onNext();
        } else {
            const outcomeSelectionDrawer = isUsingCommuniCareResolutionFlow
                ? Drawers.SelectSectionGgResolution
                : Drawers.SelectMultiOutcomeResolution;

            onNext(
                {
                    hideAccidentallyPressed: true,
                    hideFoundDevice: true,
                    hideRoutineCheck: false,
                    resident: residentData,
                    residentId,
                },
                hasNextDrawer
                    ? undefined
                    : { type: outcomeSelectionDrawer, showCloseIcon: true, fullHeight: true }
            );
        }
    };

    const handleClickLogRefusal = () => {
        trackEvent('Log Refusal Button Clicked', {
            objectType: ObjectTypes.Button,
            residentId,
            context: DrawerName,
        });

        onNext(
            {
                hideAccidentallyPressed: true,
                hideFoundDevice: true,
                hideRoutineCheck: true,
                resident: residentData,
                residentId,
                isCareRefusal: true,
                title: 'Select all care refused',
            },
            hasNextDrawer
                ? undefined
                : {
                      type: Drawers.SelectMultiOutcomeResolution,
                      showCloseIcon: true,
                      fullHeight: true,
                  }
        );
    };

    return (
        <>
            <DrawerBody sx={{ pt: 8, pb: 0.5 }}>
                <Card
                    onClick={handleClickViewProfile}
                    data-testid="view-resident-profile"
                    sx={{
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'grey.100',
                    }}
                >
                    {showActivityProgress && (
                        <Box sx={{ pt: 2.5, px: 2.5 }}>
                            <RoutineCheckChip
                                minSinceLastActivity={minSinceLastActivity}
                                isLoading={isLoadingCareActivity}
                                showPrefix
                            />
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'auto 1fr auto',
                            alignItems: 'center',
                            columnGap: 2,
                            rowGap: 2,
                            p: 2.5,
                        }}
                    >
                        <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                            <ResidentAvatar
                                resident={resident}
                                isCheckedIn={unitIsCheckedIn}
                                isPinned={isActive && isPinned}
                                progress={getRoutineCheckProgress(
                                    showActivityProgress
                                        ? {
                                              current: minSinceLastActivity,
                                              max: routineCheckInterval,
                                          }
                                        : undefined
                                )}
                                size="large"
                            />
                        </Box>
                        <Box sx={{ gridColumn: 2, gridRow: 1 }}>
                            <Typography
                                variant="h4"
                                sx={{ fontWeight: 'bold' }}
                                data-dd-privacy="mask"
                            >
                                {residentName}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'grey.900' }}>
                                {age && (
                                    <>
                                        {`Age: `}
                                        <b>{age}</b>
                                    </>
                                )}
                                {age && unit && ' • '}
                                {unit ? (
                                    <>
                                        Unit:{' '}
                                        <b>
                                            {unit.displayName}
                                            {' | '}
                                            {unit?.careType &&
                                                CareTypeLabelsAbbreviated[unit.careType]}
                                        </b>
                                    </>
                                ) : (
                                    'No Unit'
                                )}
                            </Typography>
                        </Box>
                        <Box sx={{ gridColumn: 3, gridRow: 1 }}>
                            <FontAwesomeIcon
                                icon={faChevronRight}
                                size="lg"
                                color={palette.primary.main}
                            />
                        </Box>
                        {careInfoDisplays.length > 0 && (
                            <>
                                <Divider sx={{ gridColumn: 2, gridRow: 2 }} />
                                <Box sx={{ gridColumn: 2, gridRow: 3 }}>
                                    <ResidentCareInfo careInfoDisplays={careInfoDisplays} />
                                </Box>
                            </>
                        )}
                    </Box>
                    {canSeePHI && (
                        <>
                            <Divider />
                            <Stack
                                direction="row"
                                divider={<Divider orientation="vertical" flexItem />}
                                sx={{ alignItems: 'center' }}
                            >
                                {/* DNR Display */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        flexBasis: '50%',
                                        pt: 1,
                                        pb: 1.5,
                                    }}
                                >
                                    <Stack sx={{ gap: 0.5, alignItems: 'center' }}>
                                        <Typography component="div" variant="body2">
                                            Code Status
                                        </Typography>
                                        {isLoadingResident ? (
                                            <Skeleton
                                                variant="rounded"
                                                width={60}
                                                height={30}
                                                sx={{ borderRadius: 3 }}
                                            />
                                        ) : (
                                            <ResuscitationChip
                                                resuscitationStrategy={
                                                    resuscitationStrategy as ResuscitationStrategyType
                                                }
                                            />
                                        )}
                                    </Stack>
                                </Box>

                                {/* Hospice Display */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        flexBasis: '50%',
                                        pt: 1,
                                        pb: 1.5,
                                    }}
                                >
                                    <Stack sx={{ gap: 0.5, alignItems: 'center' }}>
                                        <Typography
                                            component="div"
                                            variant="body2"
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            Hospice
                                        </Typography>
                                        {isLoadingResident ? (
                                            <Skeleton
                                                variant="rounded"
                                                width={60}
                                                height={24}
                                                sx={{ borderRadius: 3 }}
                                            />
                                        ) : (
                                            <HospiceChip
                                                hospiceStatus={validateHospiceStatus(
                                                    hospiceHealthInfo?.hospiceStatus
                                                )}
                                            />
                                        )}
                                    </Stack>
                                </Box>
                            </Stack>
                        </>
                    )}
                </Card>
            </DrawerBody>
            <DrawerFooter sx={{ gap: 2.5, justifyContent: 'center', alignItems: 'center' }}>
                {unitIsCheckInEligible ? (
                    <UnitCheckInCard
                        unit={{ ...unit, currentOccupants: [resident] }}
                        onNext={onNext}
                        analyticsPrefix={DrawerName}
                    />
                ) : isActive ? (
                    <SettingContainer
                        title="Care Assignment"
                        control={
                            <Switch
                                checked={isPinned}
                                onClick={handleTogglePinnedResident}
                                icon={<FontAwesomeIcon icon={faHeart} />}
                                checkedIcon={<FontAwesomeIcon icon={faHeart} />}
                            />
                        }
                    />
                ) : null}

                <Stack direction="row" sx={{ width: '100%', gap: 2 }}>
                    {!unitIsIL && isActive && (
                        <>
                            {!isTaskingEnabled && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    disableElevation
                                    disabled={isLoadingResident}
                                    loading={isLoadingResident}
                                    onClick={handleClickLogRefusal}
                                    sx={{ flexBasis: '50%', flexGrow: 1 }}
                                >
                                    Log Refusal
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                disableElevation
                                disabled={isLoadingResident}
                                loading={isLoadingResident}
                                onClick={handleClickLogCare}
                                sx={{ flexBasis: '50%', flexGrow: 1 }}
                            >
                                Log Care
                            </Button>
                        </>
                    )}
                </Stack>
            </DrawerFooter>
        </>
    );
}

export default ResidentDetailsDrawer;
